// WhatsAppFloatingButton.js
import React, { useEffect, useState, useCallback } from 'react';
import './MessageIcon.css'
import { FaWhatsapp } from 'react-icons/fa'; // Import the WhatsApp icon

import { IoCloseCircleOutline } from 'react-icons/io5'; // For the close icon

const WHATSAPP_BUTTON_DISMISSED_KEY = 'whatsappButtonDismissed';

const WhatsAppFloatingButton = ({
    phoneNumber,
    message = "Hey, I need to know more!",
    scrollThreshold = 200, // Pixels from top to trigger appearance
    initialDelay = 5000, // New: Delay in ms before first appearance (e.g., 5 seconds)
    showTextOnHover = true,
    hoverText = "Chat Now!",
    dotContent = "1"
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [hasBeenDismissed, setHasBeenDismissed] = useState(false);
    const [initialDelayPassed, setInitialDelayPassed] = useState(false);

    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Load dismissal state from localStorage on component mount
    useEffect(() => {
        const dismissed = localStorage.getItem(WHATSAPP_BUTTON_DISMISSED_KEY);
        if (dismissed === 'true') {
            setHasBeenDismissed(true);
        }

        // Set up the initial delay timer
        const timer = setTimeout(() => {
            setInitialDelayPassed(true);
        }, initialDelay);

        return () => clearTimeout(timer); // Cleanup timer
    }, [initialDelay]);

    // Handle scroll logic
    const handleScroll = useCallback(() => {
        if (hasBeenDismissed || !initialDelayPassed) {
            return; // Don't show if dismissed or delay hasn't passed
        }

        const currentScrollY = window.scrollY;
        // Simple scroll down detection: if current scroll is beyond threshold
        // and it was previously hidden, show it.
        if (currentScrollY > scrollThreshold) {
            setIsVisible(true);
        } else {
            // Hide if scrolled back above threshold
            setIsVisible(false);
        }
    }, [hasBeenDismissed, initialDelayPassed, scrollThreshold]);


    useEffect(() => {
        // Add scroll listener only if not dismissed and initial delay passed
        if (!hasBeenDismissed && initialDelayPassed) {
            window.addEventListener('scroll', handleScroll);
        } else {
            // Remove listener if dismissed or delay not passed
            window.removeEventListener('scroll', handleScroll);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hasBeenDismissed, initialDelayPassed, handleScroll]);

    // Handle dismissal click
    const handleDismiss = (e) => {
        e.preventDefault(); // Prevent the WhatsApp link from opening
        e.stopPropagation(); // Stop event bubbling to the parent link
        setIsVisible(false);
        setHasBeenDismissed(true);
        localStorage.setItem(WHATSAPP_BUTTON_DISMISSED_KEY, 'true');
    };

    if (hasBeenDismissed) {
        return null; // Don't render anything if dismissed
    }

    return (
        <a
            href={whatsappLink}
            className={`whatsapp-icon ${isVisible ? 'is-visible' : ''}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            <div className="whatsapp-bubble">
                <FaWhatsapp size={35} color="white" />
                <span className="red-dot">
                    {dotContent}
                </span>
                {/* Close Button */}
                <button className="whatsapp-close-btn" onClick={handleDismiss} aria-label="Close WhatsApp chat button">
                    <IoCloseCircleOutline size={20} color="white" />
                </button>
            </div>
            {showTextOnHover && <span className="whatsapp-text">{hoverText}</span>}
        </a>
    );
};

export default WhatsAppFloatingButton;